import React, { SyntheticEvent, useEffect, useState } from 'react';
import { AutocompleteRenderInputParams, Box, TextField } from '@mui/material';
import api from '../../../../../services/api';
import { readOnlyEntities } from '../../../../../utils/commonVariables/entityKeys';
import { buildOdataEndPoint } from '../../../../../utils/odataEndPoint';
import { ICountry } from '../../../../../types/entities/ICountry';
import { useNotification } from '../../../../../contexts/NotificationsContext';
import { useZinantisMembers } from '../../../../contextsProperty/ContextZinantisMembers';
import LoadedOptionsTypeahead from '../../../../../muiComponents/LoadedOptionsTypeahead';
import Flag from '../../../components/Flag';
import useTranslate from '../../../../../customHooks/useTranslate';
import { typeaheadFilterInputStyles } from '../../../../../utils/zinantis/members/membersUtils';
import { FeedbackMessages } from '../../../../../utils/commonVariables/messages';

interface CountryState {
    label: string;
    id: number;
    iso2: string;
}

const TypeaheadCountries = () => {
    const { setErrorMessage } = useNotification();
    const translate = useTranslate();
    const { updateFilters, filters } = useZinantisMembers();
    const [countries, setCountries] = useState<CountryState[]>([]);

    useEffect(() => {
        const getCountries = async () => {
            try {
                const enpointOptions = {
                    entity: readOnlyEntities.Country,
                    filter: 'Active',
                    orderBy: 'LocalizedCaption asc',
                    select: [
                        'Id',
                        'Name',
                        'LocalizedCaption',
                        'Caption',
                        'ISO2',
                    ],
                };
                const endpoint = buildOdataEndPoint(enpointOptions);
                const { data } = await api.get(endpoint);
                setCountries(
                    data.value.map((country: ICountry) => ({
                        label:
                            country.LocalizedCaption ||
                            country.Caption ||
                            country.Name,
                        id: parseInt(country.Id),
                        iso2: country.ISO2.toLowerCase(),
                    }))
                );
            } catch (error) {
                setErrorMessage(FeedbackMessages.ERROR_FETCHING);
            }
        };

        getCountries();
    }, []);

    return (
        <LoadedOptionsTypeahead
            idValueKey='id'
            className='card-component'
            sx={{ padding: 0 }}
            options={countries}
            loading={!countries.length}
            renderOption={(
                props: React.HTMLAttributes<HTMLLIElement> & {
                    key: string;
                },
                option: CountryState
            ) => {
                const { key, ...optionProps } = props;
                return (
                    <Box key={key} component='li' {...optionProps}>
                        <Flag code={option.iso2} text={option.label} />
                        <p className='margin-inline10'>{option.label}</p>
                    </Box>
                );
            }}
            value={
                filters.idCountry
                    ? countries.find(c => c.id === filters.idCountry)
                    : null
            }
            handleChange={(
                _event: SyntheticEvent<Element, Event>,
                newValue: CountryState | null
            ) => {
                updateFilters('idCountry', newValue?.id || 0);
            }}
            renderInput={(params: AutocompleteRenderInputParams) => (
                <TextField
                    variant='standard'
                    {...params}
                    placeholder={translate('country')}
                    sx={typeaheadFilterInputStyles}
                />
            )}
        />
    );
};

export default TypeaheadCountries;
